import React, {FC, memo} from "react";
import moment from "moment";
import {Button, Popconfirm, Tooltip} from "antd";
import {CheckCircleOutlined, DeleteOutlined, EditOutlined, EuroCircleOutlined} from "@ant-design/icons";
import {blue, green, red} from "@ant-design/colors";
import {FormikProps} from "formik";
import {ScheduledPaymentType} from "./PaymentsInteractive";
import {useCurrency} from "../../../../helpers/currencyHelper";
import css from "./PaymentLists.less";
import {renderAmount} from "../../../../helpers/renderAmount";
import {Currency, PaymentType, UpdateCourseAndBookingsInput} from "../../../../generated/graphql";
import {PreviewBookingType} from "../PreviewBooking";
import {colorGrey} from "../../../../styles/colors";
import classNames from "classnames";

type RenderMonthlyPaymentEntryProps = {
    formikProps: FormikProps<UpdateCourseAndBookingsInput>;
    rate: ScheduledPaymentType;
    rowIndex: number;
    onEdit: (index: number) => void;
    booking: PreviewBookingType;
    disabled?: boolean;
    isChanged?: boolean;
};

const RenderMonthlyPaymentEntry: FC<RenderMonthlyPaymentEntryProps> = ({formikProps, rate, rowIndex, onEdit, booking, disabled = false, isChanged = false}) => {
    const currency = useCurrency();
    const bookingId = booking.id;
    const currentBookingsData = formikProps.values.updatedBookingsData ?? [];
    const currentBooking = currentBookingsData.find((bookingX) => bookingX.id === bookingId);
    const paymentType = currentBooking?.paymentType;
    // const scheduledPayments = currentBooking?.scheduledPayments ?? [];
    const scheduledPayments = booking?.scheduledPayments ?? currentBooking?.scheduledPayments ?? [];
    const paymentDone = Boolean(rate.paymentDone);

    if (!currentBooking) {
        return null;
    }

    const handleDelete = (/* paymentId: string */) => {
        const updatedScheduledPayments = scheduledPayments.filter((payment) => payment.id !== rate.id);

        currentBooking.scheduledPayments = updatedScheduledPayments;

        const updatedBookingsData = currentBookingsData.map((booking) => {
            if (booking.id === currentBooking.id) {
                return currentBooking;
            }

            return booking;
        });

        formikProps.setFieldValue("updatedBookingsData", updatedBookingsData);
    };

    return (
        <div key={rate.paymentDue} className={classNames(css.monthlyPaymentEntry, isChanged && css.changedPayment)}>
            <div className={rate.paymentDone ? css.mainDone : css.mainDue}>
                <div className={css.month}>
                    <span className={css.num}>{`${rowIndex + 1}.`}</span>
                    <span>{moment(rate.paymentDue).format("MMM YY")}</span>
                </div>
                {paymentType === PaymentType.Custom && !disabled && (
                    <div className={css.editButtons}>
                        {scheduledPayments.length > 1 && (
                            <Tooltip title={paymentDone ? "Bezahlte Rate kann nicht gelöscht werden" : ""}>
                                <Button
                                    size="small"
                                    type="text"
                                    style={paymentDone ? undefined : {color: red[4]}}
                                    onClick={handleDelete}
                                    disabled={paymentDone}
                                >
                                    <DeleteOutlined />
                                </Button>
                            </Tooltip>
                        )}
                        <Tooltip title={paymentDone ? "Bezahlte Rate kann nicht bearbeitet werden" : ""}>
                            <Button
                                size="small"
                                type="text"
                                style={paymentDone ? undefined : {color: blue[5]}}
                                onClick={() => onEdit(rowIndex)}
                                disabled={paymentDone}
                            >
                                <EditOutlined />
                            </Button>
                        </Tooltip>
                    </div>
                )}
                {rate.paymentDone ? (
                    <div className={css.paymentDoneContainer}>
                        <div className={css.paymentDone}>{`Bezahlt am ${moment(rate.paymentDone).format("DD.MM.YYYY")}`}</div>
                        <CheckCircleOutlined style={{color: green[5], marginTop: -2, /* paddingRight: 4, */ fontSize: 13}} />
                    </div>
                ) : (
                    <div className={css.paymentDueContainer}>
                        <div className={css.paymentDue}>{`Fällig am ${moment(rate.paymentDue).format("DD.MM.YYYY")}`}</div>
                        <EuroCircleOutlined style={{color: colorGrey, /* marginTop: -2, */ fontSize: 13}} />
                    </div>
                )}
                <div className={css.amount}>
                    {renderAmount({
                        amount: rate.paymentAmount ?? 0,
                        currency: currency.short as Currency,
                    })}
                </div>
            </div>
            {/* {renderHint()} */}
        </div>
    );
};

export default memo(RenderMonthlyPaymentEntry);
